import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, MouseEvent, RefObject, SetStateAction } from "react";
import { Info, RotateCcw, Volume2, VolumeX } from "lucide-react";
import type { RiskFlowAlert } from "../../lib/riskflow-feed";
import type { RiskFlowVisualCandidate } from "../../lib/riskflow-visual-enrichment";
import { extractYouTubeVideoId } from "../../lib/youtube";
import type { RiskFlowPostSurface } from "./RiskFlowPostCard";
import { RiskFlowEconHistoryFrame } from "./RiskFlowEconHistoryFrame";
import { RiskFlowInfoBackCard } from "./RiskFlowInfoBackCard";
import { RiskFlowStockChartFrame } from "./RiskFlowStockChartFrame";
import { RiskFlowTickerCycler } from "./RiskFlowTickerCycler";

interface RiskFlowVisualFrameProps {
  alert: RiskFlowAlert;
  surface: RiskFlowPostSurface;
  activeVisual: RiskFlowVisualCandidate | null;
  visualCandidates: RiskFlowVisualCandidate[];
  visualIndex: number;
  onVisualIndexChange: Dispatch<SetStateAction<number>>;
  fallbackToInfo?: boolean;
  onRequestBreakdown?: () => void;
}

export function RiskFlowVisualFrame({
  alert,
  surface,
  activeVisual,
  visualCandidates,
  visualIndex,
  onVisualIndexChange,
  fallbackToInfo = false,
  onRequestBreakdown,
}: RiskFlowVisualFrameProps) {
  const [isMuted, setIsMuted] = useState(true);
  const [showInfoBack, setShowInfoBack] = useState(fallbackToInfo);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    setShowInfoBack(fallbackToInfo);
  }, [alert.id, fallbackToInfo]);

  const toggleMute = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      const nextMuted = !isMuted;
      const video = videoRef.current;
      if (video) {
        video.muted = nextMuted;
        video.volume = nextMuted ? 0 : 1;
        if (!nextMuted) {
          void video.play().catch(() => {
            video.muted = true;
            video.volume = 0;
            setIsMuted(true);
          });
        }
      }
      setIsMuted(nextMuted);
    },
    [isMuted],
  );
  const handleVisualUnavailable = useCallback(() => {
    if (visualIndex < visualCandidates.length - 1) {
      onVisualIndexChange((prev) => prev + 1);
      return;
    }
    onVisualIndexChange(visualCandidates.length);
  }, [onVisualIndexChange, visualCandidates.length, visualIndex]);
  const playableVideoUrl =
    alert.videoUrl && !extractYouTubeVideoId(alert.videoUrl)
      ? alert.videoUrl
      : null;
  const hasMediaLayer = Boolean(playableVideoUrl || activeVisual);
  const shouldShowInfo = fallbackToInfo || showInfoBack;

  const toggleInfoBack = useCallback((event: MouseEvent) => {
    event.stopPropagation();
    setShowInfoBack((current) => !current);
  }, []);

  if (!hasMediaLayer && !fallbackToInfo) return null;

  return (
    <div className="relative block overflow-hidden rounded-[6px] border border-[var(--fintheon-accent)]/10 bg-[var(--fintheon-bg)]">
      <div className="grid">
        {hasMediaLayer ? (
          <div
            className={`col-start-1 row-start-1 transition-[opacity,transform,filter] duration-300 ease-out ${
              shouldShowInfo
                ? "pointer-events-none scale-[0.985] opacity-0 blur-[1px]"
                : "scale-100 opacity-100 blur-0"
            }`}
          >
            {playableVideoUrl ? (
              <VideoLayer
                alert={alert}
                surface={surface}
                isMuted={isMuted}
                videoRef={videoRef}
                onVolumeChange={setIsMuted}
                onToggleMute={toggleMute}
              />
            ) : (
              <VisualLayer
                activeVisual={activeVisual}
                surface={surface}
                onUnavailable={handleVisualUnavailable}
              />
            )}
          </div>
        ) : null}
        <div
          className={`col-start-1 row-start-1 transition-[opacity,transform,filter] duration-300 ease-out ${
            shouldShowInfo
              ? "scale-100 opacity-100 blur-0"
              : "pointer-events-none scale-[1.012] opacity-0 blur-[1px]"
          }`}
          aria-hidden={!shouldShowInfo}
        >
          <RiskFlowInfoBackCard
            alert={alert}
            surface={surface}
            onRequestBreakdown={onRequestBreakdown}
          />
        </div>
      </div>

      {hasMediaLayer ? (
        <button
          type="button"
          onClick={toggleInfoBack}
          className="solvys-liquid-glass fintheon-black-liquid-glass fintheon-black-liquid-glass--absolute absolute top-2 right-2 z-10 flex h-8 w-8 items-center justify-center rounded-full text-[var(--fintheon-text)]/78 transition-[color,text-shadow,transform] hover:text-[var(--fintheon-accent)] hover:[text-shadow:0_0_12px_color-mix(in_srgb,var(--fintheon-accent)_62%,transparent)] focus:outline-none focus-visible:text-[var(--fintheon-accent)]"
          aria-label={shouldShowInfo ? "Show media" : "Show headline context"}
          data-riskflow-info-back-trigger="true"
          data-active={shouldShowInfo ? "true" : "false"}
        >
          {shouldShowInfo ? (
            <RotateCcw className="h-3.5 w-3.5" />
          ) : (
            <Info className="h-3.5 w-3.5" />
          )}
        </button>
      ) : null}

      {activeVisual && !shouldShowInfo ? (
        <RiskFlowTickerCycler
          visualCandidates={visualCandidates}
          visualIndex={visualIndex}
          onVisualIndexChange={onVisualIndexChange}
        />
      ) : null}
    </div>
  );
}

interface VideoLayerProps {
  alert: RiskFlowAlert;
  surface: RiskFlowPostSurface;
  isMuted: boolean;
  videoRef: RefObject<HTMLVideoElement | null>;
  onVolumeChange: (muted: boolean) => void;
  onToggleMute: (event: MouseEvent) => void;
}

function VideoLayer({
  alert,
  surface,
  isMuted,
  videoRef,
  onVolumeChange,
  onToggleMute,
}: VideoLayerProps) {
  const playableVideoUrl =
    alert.videoUrl && !extractYouTubeVideoId(alert.videoUrl)
      ? alert.videoUrl
      : null;
  if (!playableVideoUrl) return null;
  return (
    <div className="relative">
      <video
        ref={videoRef}
        src={playableVideoUrl}
        poster={alert.imageUrl ?? undefined}
        muted={isMuted}
        loop
        autoPlay
        playsInline
        preload="metadata"
        className={`block w-full ${mediaHeight(surface)}`}
        onVolumeChange={(e) => onVolumeChange(e.currentTarget.muted)}
        onError={(e) => {
          const wrapper = e.currentTarget.parentElement;
          if (wrapper) wrapper.style.display = "none";
        }}
      />
      <button
        type="button"
        onClick={onToggleMute}
        className="solvys-liquid-glass fintheon-black-liquid-glass fintheon-black-liquid-glass--absolute absolute bottom-2 left-2 flex h-7 w-7 items-center justify-center rounded-full text-[var(--fintheon-text)]/90 transition-[color,text-shadow] hover:text-[var(--fintheon-text)] hover:[text-shadow:0_0_10px_color-mix(in_srgb,var(--fintheon-text)_45%,transparent)] focus:outline-none focus-visible:text-[var(--fintheon-text)]"
        aria-label={isMuted ? "Unmute" : "Mute"}
      >
        {isMuted ? (
          <VolumeX className="h-3.5 w-3.5" />
        ) : (
          <Volume2 className="h-3.5 w-3.5" />
        )}
      </button>
    </div>
  );
}

interface VisualLayerProps {
  activeVisual: RiskFlowVisualCandidate | null;
  surface: RiskFlowPostSurface;
  onUnavailable: () => void;
}

function VisualLayer({
  activeVisual,
  surface,
  onUnavailable,
}: VisualLayerProps) {
  if (!activeVisual) return null;
  return activeVisual.kind === "econ-history" && activeVisual.econPrint ? (
    <RiskFlowEconHistoryFrame
      analysis={activeVisual.econPrint}
      surface={surface}
    />
  ) : activeVisual.kind === "stock-chart" && activeVisual.ticker ? (
    <RiskFlowStockChartFrame
      ticker={activeVisual.ticker}
      href={activeVisual.href}
      label={activeVisual.label}
      surface={surface}
    />
  ) : (
    <a
      href={activeVisual.href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={activeVisual.label}
      data-riskflow-visual-source={activeVisual.source}
      className="block"
    >
      <img
        src={activeVisual.imageUrl}
        alt=""
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={onUnavailable}
        className={`block w-full ${
          activeVisual.fit === "contain" ? "object-contain p-2" : "object-cover"
        } ${mediaHeight(surface)}`}
      />
    </a>
  );
}

function mediaHeight(surface: RiskFlowPostSurface): string {
  return surface === "mini" ? "max-h-40" : "max-h-72";
}
